import { type ReactNode, createContext, useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { getFirebaseAuth } from './firebase';
import { setAuthTokenProvider } from '@/lib/apiClient';
import { firebaseClientConfigured } from '@/lib/env';
import { LoadingState } from '@/components/shared/LoadingState';

interface AuthState {
  status: 'resolving' | 'unauthenticated' | 'authenticated' | 'misconfigured';
  user: User | null;
  claims: Record<string, unknown>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthGate>');
  return ctx;
}

interface AuthGateProps {
  children: ReactNode;
}

export function AuthGate({ children }: AuthGateProps): JSX.Element {
  const [state, setState] = useState<AuthState>({
    status: firebaseClientConfigured ? 'resolving' : 'misconfigured',
    user: null,
    claims: {},
  });

  useEffect(() => {
    const auth = getFirebaseAuth();
    if (!auth) return;

    setAuthTokenProvider(async () => {
      const u = auth.currentUser;
      if (!u) return null;
      return u.getIdToken(false);
    });

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setState({ status: 'unauthenticated', user: null, claims: {} });
        return;
      }
      const tokenResult = await user.getIdTokenResult(true);
      setState({ status: 'authenticated', user, claims: tokenResult.claims });
    });

    return () => unsub();
  }, []);

  if (state.status === 'resolving') {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <LoadingState message="Verifica sessione…" />
      </div>
    );
  }

  if (state.status === 'misconfigured') {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-3 bg-background p-6 text-center">
        <h1 className="text-xl font-semibold text-foreground">Auth non configurata</h1>
        <p className="max-w-md text-sm text-muted-foreground">
          Le variabili VITE_FIREBASE_* non sono valorizzate. Copia
          <code className="mx-1 rounded-sm bg-muted px-1 py-0.5 font-mono text-xs">.env.example</code>
          in <code className="mx-1 rounded-sm bg-muted px-1 py-0.5 font-mono text-xs">.env.local</code> e
          inserisci le credenziali Firebase del progetto backoffice.
        </p>
      </div>
    );
  }

  if (state.status === 'unauthenticated') {
    // The login surface lives at `/login`, mounted in the top-level
    // dispatcher (App.tsx) OUTSIDE this gate. Redirect there with
    // `replace` so the back button doesn't bounce the user back to a
    // gated route they cannot reach. Loop avoidance: `/login` is not
    // wrapped by AuthGate, so this Navigate never re-enters here.
    return <Navigate to="/login" replace />;
  }

  return (
    <AuthContext.Provider value={state}>
      {children}
    </AuthContext.Provider>
  );
}
